import {
  inject,
  Injectable,
  Renderer2,
  RendererFactory2,
  DOCUMENT,
} from '@angular/core';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { NavigationStart, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Article,
  SchemaOrg,
  SeoData,
  TwitterCardType,
} from '@edt/models/seo-data';

interface ImageParams {
  width: number;
  height: number;
  format: 'jpg' | 'png' | 'webp';
  fit: 'clip' | 'crop' | 'fill' | 'facearea' | 'fit' | 'scale';
}

export const OG_IMAGE_PARAMS: ImageParams = {
  width: 1200,
  height: 630,
  format: 'png',
  fit: 'crop',
};

export const TWITTER_IMAGE_PARAMS: ImageParams = {
  width: 1200,
  height: 600,
  format: 'png',
  fit: 'crop',
};

const SITE_NAME = 'Mi Tienda';
const TWITTER_HANDLE = '@mitienda';

const GLOBAL_SCHEMA_SCRIPT_ID = 'jsonld-global';
const PAGE_SCHEMA_SCRIPT_ID = 'jsonld-page';

type MetaAttrType = 'name' | 'property';

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly _titleService = inject(Title);
  private readonly _metaService = inject(Meta);
  private readonly _document = inject(DOCUMENT);
  private readonly _renderer: Renderer2;
  private readonly _rendererFactory = inject(RendererFactory2);
  private readonly _router = inject(Router, { optional: true });

  constructor() {
    this._renderer = this._rendererFactory.createRenderer(null, null);

    this._router?.events.pipe(takeUntilDestroyed()).subscribe(event => {
      if (event instanceof NavigationStart) {
        this.removeSchema(PAGE_SCHEMA_SCRIPT_ID);
      }
    });
  }

  private get _urlObj(): URL {
    return new URL(this._document.URL);
  }

  setSeoData(seoData: SeoData, useBaseUrl = false): void {
    const baseUrl = `${this._urlObj.protocol}//${this._urlObj.host}`;
    const title = seoData.title ? `${seoData.title}` : SITE_NAME;

    this._titleService.setTitle(title);

    const seoDescription = seoData.description?.slice(
      0,
      Math.min(seoData.description.length - 1, 159)
    );

    this._updateMetaTag('description', seoDescription, 'name');
    this._updateMetaTag('keywords', seoData.keywords, 'name');
    this._updateMetaTag(
      'robots',
      seoData.noIndex ? 'noindex,nofollow' : 'index,follow',
      'name'
    );

    this._updateMetaTag('og:locale', 'es_US');
    this._updateMetaTag('og:site_name', seoData.site);
    this._updateMetaTag('og:title', title);
    this._updateMetaTag('og:description', seoDescription);
    const imageUrl = this._resolveImageUrl(
      seoData.ogImage?.url,
      baseUrl,
      useBaseUrl
    );
    this._updateMetaTag('og:image', imageUrl);
    this._updateMetaTag('og:image:secure_url', imageUrl);
    this._updateMetaTag('og:image:width', seoData.ogImage?.width ?? '1200');
    this._updateMetaTag('og:image:height', seoData.ogImage?.height ?? '630');
    this._updateMetaTag('og:image:type', seoData.ogImage?.type ?? 'image/png');
    this._updateMetaTag('og:image:alt', seoData.ogImage?.alt);
    this._updateMetaTag('og:url', seoData.ogUrl);
    this._updateMetaTag('og:type', seoData.ogType);

    this._updateMetaTag('twitter:site', TWITTER_HANDLE, 'name');
    this._updateMetaTag('twitter:url', seoData.ogUrl, 'name');
    const cardType: TwitterCardType =
      seoData.twitterCardType ?? 'summary_large_image';
    this._updateMetaTag('twitter:card', cardType, 'name');
    this._updateMetaTag('twitter:title', title, 'name');
    this._updateMetaTag('twitter:description', seoDescription, 'name');
    this._updateMetaTag(
      'twitter:image',
      this._resolveImageUrl(seoData.ogImage?.url, baseUrl, useBaseUrl),
      'name'
    );
    this._updateMetaTag('twitter:image:alt', seoData.ogImage?.alt, 'name');

    this._updateMetaTag('twitter:label1', seoData.twitterLabel?.key, 'name');
    this._updateMetaTag('twitter:data1', seoData.twitterLabel?.value, 'name');
    this._updateMetaTag('twitter:label2', seoData.twitterLabel2?.key, 'name');
    this._updateMetaTag('twitter:data2', seoData.twitterLabel2?.value, 'name');

    this._updateCanonicalUrl(seoData.ogUrl);
  }

  setArticleMetadata(article: Article): void {
    this._updateMetaTag('article:published_time', article.publishedAt);

    if (article.modifiedAt) {
      this._updateMetaTag('article:modified_time', article.modifiedAt);
    }
    if (article.expirationTime) {
      this._updateMetaTag('article:expiration_time', article.expirationTime);
    }

    this._updateMetaTag('article:author', article.author);
    this._updateMetaTag('article:section', article.category);

    // FIX: limpia tags del artículo anterior para evitar que queden huérfanos
    this._metaService.removeTag('property="article:tag"');
    article.tags.forEach(tag => this._updateMetaTag('article:tag', tag));
  }

  private _resolveImageUrl(
    url: string | undefined,
    baseUrl: string,
    useBaseUrl: boolean
  ): string | undefined {
    if (!url) return undefined;
    return useBaseUrl ? `${baseUrl}${url}` : url;
  }

  private _updateMetaTag(
    name: string,
    content?: string,
    attr: MetaAttrType = 'property'
  ): void {
    if (!content) {
      this._metaService.removeTag(`${attr}="${name}"`);
      return;
    }

    // FIX: elimina duplicados legacy con el atributo opuesto antes de inyectar.
    // Ej: si va a inyectar property="og:title", remueve name="og:title" si existe.
    const oppositeAttr: MetaAttrType = attr === 'name' ? 'property' : 'name';
    this._metaService.removeTag(`${oppositeAttr}="${name}"`);

    this._metaService.updateTag({ [attr]: name, content } as MetaDefinition);
  }

  private _updateCanonicalUrl(url?: string): void {
    const existingCanonicalUrl = this._document.querySelector(
      'link[rel="canonical"]'
    );
    if (existingCanonicalUrl) existingCanonicalUrl.remove();

    if (url) {
      const canonicalLink = this._document.createElement('link');
      canonicalLink.rel = 'canonical';
      canonicalLink.href = url;
      this._document.head.appendChild(canonicalLink);
    }
  }

  setGlobalJsonLd(schemas: SchemaOrg[]): void {
    this._setSchema(GLOBAL_SCHEMA_SCRIPT_ID, schemas);
  }

  setJsonLdGraph(schemas: SchemaOrg[]): void {
    this._setSchema(PAGE_SCHEMA_SCRIPT_ID, schemas);
  }

  removeSchema(scriptId: string): void {
    const existing = this._document.getElementById(scriptId);
    if (existing) {
      this._renderer.removeChild(this._document.head, existing);
    }
  }

  setJsonLd(data: SchemaOrg, scriptId: string): void {
    this._setSchema(scriptId, [data]);
  }

  private _setSchema(scriptId: string, schemas: SchemaOrg[]): void {
    if (!schemas.length) {
      this.removeSchema(scriptId);
      return;
    }

    const clean = schemas.map(schema => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { ['@context']: _omit, ...rest } = schema as Record<
        string,
        unknown
      >;
      return rest;
    });

    const payload =
      clean.length === 1
        ? { '@context': 'https://schema.org', ...clean[0] }
        : { '@context': 'https://schema.org', '@graph': clean };

    this.removeSchema(scriptId);

    const script = this._renderer.createElement('script');
    this._renderer.setAttribute(script, 'id', scriptId);
    this._renderer.setAttribute(script, 'type', 'application/ld+json');
    this._renderer.setProperty(script, 'text', JSON.stringify(payload));
    this._renderer.appendChild(this._document.head, script);
  }
}
